/**
 * The intro: the timed opening sequence of star, flare, implosion, supernova
 * and the globe forming. It runs once per visit.
 *
 * It is a closed-form function of the time `t` (seconds since it began), so it
 * can render any `t` directly and `?t=` scrubs exactly. Pieces: the star and
 * the wordmark before the burst, the supernova's flash and shock fronts, the
 * dark planet inflating with the shell of lights, the faint towns, the
 * particles, and the sun.
 */
import { smoothstep } from '../easing'
import {
  CRESCENT,
  CRESCENT_IN,
  DAY_DIM,
  drawSun,
  HOME_LIGHT,
  LIGHT_DEG,
  SUN,
  SUN_IN,
} from '../backlight'
import { LAND_IN, longitudeAt, METRO_GLOW, radiusAt, TILT, TOWN_GLOW } from '../globe-look'
import type { Renderer } from '../renderer/renderer'
import { frameOf, type Viewport } from '../viewport'
import { drawStar, measureArms, SQUARE_ARMS, STAR_FONT, type StarArm } from './star'
import { drawBlast, SHELL_SETTLED, seedParticles, shellAt, T_DET, trailAt } from './supernova'
import { createWordmark } from './wordmark'

const RAD = Math.PI / 180

/* =====================================================================
   TWEAKABLES. Times are seconds.
   ===================================================================== */
const DISC_IN: [number, number] = [
  0.9, 2.4,
] /* the dark sphere fills in behind the lights (s after the burst) */
const TOWN_IN: [number, number] = [1.6, 3.2] /* the faint layer of all towns comes up */
const INTRO_TAIL = 0.45 /* s the set globe rests in the centre before it starts to glide */
/* ===================================================================== */

/** How long the intro runs: until every light has set, and the globe has rested a moment. */
export const INTRO_DURATION = T_DET + SHELL_SETTLED + INTRO_TAIL

export interface Intro {
  /** Draws the intro at time `t`. */
  render(t: number, vp: Viewport): void
  /** Seeds the shell and ejecta afresh and hands them to the renderer. `small` is for phones: fewer particles. */
  reseed(small: boolean): void
  /** Measures the wordmark's X and its arm at the current type size. Again once the face has loaded. */
  measureWordmark(): void
  /** Measures the star's arms off the Manrope letterform, keeping the last ones if it cannot. */
  measureArms(): void
  /** The face the measuring needs: wait for it to load, then measure again. */
  readonly font: string
  /** Hides the wordmark, as the intro hands over. */
  hideWordmark(): void
}

export function createIntro(gl: Renderer): Intro {
  const ctx = gl.ctx
  const wordmark = createWordmark()
  let arms: readonly StarArm[] = SQUARE_ARMS

  return {
    font: STAR_FONT,
    measureArms: measureStarArms,
    hideWordmark: wordmark.hide,
    measureWordmark() {
      wordmark.measure(arms)
    },
    reseed(small) {
      gl.setParticles(seedParticles(small))
    },
    render(t, vp) {
      ctx.setTransform(vp.dpr, 0, 0, vp.dpr, 0, 0)
      ctx.globalCompositeOperation = 'source-over'
      ctx.globalAlpha = 1
      gl.begin(frameOf(vp, HOME_LIGHT))
      const tau = t - T_DET

      /* ---- 1. the point, its flare into the word's X, the implosion ---- */
      ctx.globalCompositeOperation = 'lighter'
      if (tau < 0) drawStar(ctx, vp, arms, wordmark.armLength(), t)
      wordmark.update(vp, t)

      if (tau >= 0) {
        drawBlast(ctx, vp, tau)

        const radius = radiusAt(tau) * vp.min
        const lam = longitudeAt(tau)

        /* ---- 2. the dark planet inflates with the shell: the land, the backlit crescent ---- */
        const disc = smoothstep(DISC_IN[0], DISC_IN[1], tau)
        const shellRadius = radius * shellAt(tau)
        const backlight = CRESCENT * smoothstep(CRESCENT_IN[0], CRESCENT_IN[1], tau)
        const dayK = DAY_DIM * backlight
        if (disc > 0.003) {
          gl.planet(
            { cx: vp.cx, cy: vp.cy, Rs: shellRadius, d: 1e5, lam, tilt: TILT },
            {
              disc,
              land: disc * smoothstep(LAND_IN[0], LAND_IN[1], tau),
              rim: disc,
              lights: [[LIGHT_DEG * RAD, backlight * disc]],
            }
          )
        }

        /* ---- 3. under the particles, the faint light of every town ---- */
        const town = smoothstep(TOWN_IN[0], TOWN_IN[1], tau)
        const globe = { cx: vp.cx, cy: vp.cy, Rs: radius, d: 1e5, lam, tilt: TILT }
        gl.setNear([])
        gl.setShade(0)
        gl.towns(globe, TOWN_GLOW * town, dayK)
        gl.metros(globe, METRO_GLOW * town, dayK)

        /* ---- 4. the particles: lights, then ejecta, each with its trail ---- */
        gl.sparks(tau, trailAt(tau), dayK)

        /* ---- 5. the sun, over everything ---- */
        drawSun(
          ctx,
          vp,
          HOME_LIGHT,
          vp.cx,
          vp.cy,
          shellRadius,
          SUN * smoothstep(SUN_IN[0], SUN_IN[1], tau) * disc,
          t
        )
      }

      /* grain last */
      gl.grain(0.35)
      gl.end()
    },
  }

  function measureStarArms() {
    arms = measureArms() ?? arms
  }
}
